import { convertTokenAddress } from 'config/tokens'
import {
  PendingDepositData,
  PendingWithdrawalData,
  getPendingDepositKey,
  getPendingWithdrawalKey,
  useSyntheticsEvents,
} from 'context/SyntheticsEvents'
import {
  MarketsInfoData,
  getMarketIndexName,
  getMarketPoolName,
} from 'domain/synthetics/markets'
import { TokenData, TokensData } from 'domain/synthetics/tokens'
import { useChainId } from 'rfx/lib/chains'
import { getByKey } from 'rfx/lib/objects'
import { useEffect, useMemo, useState } from 'react'
import { useToastAutoClose } from './useToastAutoClose'
import { StatusNotification } from './StatusNotification'
import {
  TransactionStatus,
  TransactionStatusType,
} from '@components/shared/TransactionStatus'
import { useTranslation } from 'react-i18next'

export type Props = {
  toastTimestamp: number
  pendingDepositData?: PendingDepositData
  pendingWithdrawalData?: PendingWithdrawalData
  marketsInfoData?: MarketsInfoData
  tokensData?: TokensData
}

export function GmStatusNotification({
  toastTimestamp,
  pendingDepositData,
  pendingWithdrawalData,
  marketsInfoData,
  tokensData,
}: Props) {
  const { chainId } = useChainId()
  const {
    depositStatuses,
    withdrawalStatuses,
    setDepositStatusViewed,
    setWithdrawalStatusViewed,
  } = useSyntheticsEvents()

  const isDeposit = Boolean(pendingDepositData)

  const [depositStatusKey, setDepositStatusKey] = useState<string>()
  const [withdrawalStatusKey, setWithdrawalStatusKey] = useState<string>()
  const { t } = useTranslation(['errors', 'trade', 'pools'])
  const depositStatus = getByKey(depositStatuses, depositStatusKey)
  const withdrawalStatus = getByKey(withdrawalStatuses, withdrawalStatusKey)

  const isCompleted = isDeposit
    ? Boolean(depositStatus?.executedTxnHash)
    : Boolean(withdrawalStatus?.executedTxnHash)

  const hasError = isDeposit
    ? Boolean(depositStatus?.cancelledTxnHash)
    : Boolean(withdrawalStatus?.cancelledTxnHash)

  const pendingDepositKey = useMemo(() => {
    if (pendingDepositData) {
      return getPendingDepositKey(pendingDepositData)
    }
  }, [pendingDepositData])

  const pendingWithdrawalKey = useMemo(() => {
    if (pendingWithdrawalData) {
      return getPendingWithdrawalKey(pendingWithdrawalData)
    }
  }, [pendingWithdrawalData])

  const title = useMemo(() => {
    if (isDeposit) {
      if (!pendingDepositData) {
        return `Unknown buy RP order`
      }

      let longToken: TokenData | undefined
      let shortToken: TokenData | undefined

      if (pendingDepositData.initialLongTokenAmount.gt(0)) {
        longToken = getByKey(
          tokensData,
          convertTokenAddress(
            chainId,
            pendingDepositData.initialLongTokenAddress,
            pendingDepositData.shouldUnwrapNativeToken ? 'native' : 'wrapped',
          ),
        )
      }

      if (pendingDepositData.initialShortTokenAmount.gt(0)) {
        shortToken = getByKey(
          tokensData,
          convertTokenAddress(
            chainId,
            pendingDepositData.initialShortTokenAddress,
            pendingDepositData.shouldUnwrapNativeToken ? 'native' : 'wrapped',
          ),
        )
      }

      const marketInfo = getByKey(
        marketsInfoData,
        pendingDepositData.marketAddress,
      )
      const indexName = marketInfo ? getMarketIndexName(marketInfo) : ''
      const poolName = marketInfo ? getMarketPoolName(marketInfo) : ''

      let tokensText = ''
      if (marketInfo?.isSameCollaterals) {
        tokensText = longToken?.symbol ?? ''
      } else {
        tokensText = [longToken, shortToken]
          .filter(Boolean)
          .map((token) => token?.symbol)
          .join(' and ')
      }

      return (
        <>
          <div className="inline-flex">
            Buying RP:&nbsp;<span>{indexName}</span>
            <span className="subtext gm-toast">[{poolName}]</span>
          </div>{' '}
          {tokensText && <span>with {tokensText}</span>}
        </>
      )
    } else {
      if (!pendingWithdrawalData) {
        return `Unknown sell RP order`
      }

      const marketInfo = getByKey(
        marketsInfoData,
        pendingWithdrawalData.marketAddress,
      )
      const indexName = marketInfo ? getMarketIndexName(marketInfo) : ''
      const poolName = marketInfo ? getMarketPoolName(marketInfo) : ''

      return (
        <>
          <div className="inline-flex">
            Selling RP:&nbsp;<span>{indexName}</span>
            <span className="subtext gm-toast">[{poolName}]</span>
          </div>
        </>
      )
    }
  }, [
    chainId,
    isDeposit,
    marketsInfoData,
    pendingDepositData,
    pendingWithdrawalData,
    tokensData,
  ])

  const creationStatus = useMemo(() => {
    let text = ''
    let status: TransactionStatusType = 'loading'
    let createdTxnHash: string | undefined

    if (isDeposit) {
      text = t('pools:sending-buy-request')

      if (depositStatus?.createdTxnHash) {
        text = t('pools:buy-request-sent')
        status = 'success'
        createdTxnHash = depositStatus?.createdTxnHash
      }
    } else {
      text = `Sending Sell request`

      if (withdrawalStatus?.createdTxnHash) {
        text = t('pools:sell-request-sent')
        status = 'success'
        createdTxnHash = withdrawalStatus?.createdTxnHash
      }
    }

    return (
      <TransactionStatus status={status} txnHash={createdTxnHash} text={text} />
    )
  }, [
    depositStatus?.createdTxnHash,
    isDeposit,
    withdrawalStatus?.createdTxnHash,
  ])

  const executionStatus = useMemo(() => {
    let text = ''
    let status: TransactionStatusType = 'loading'
    let txnHash: string | undefined

    if (isDeposit) {
      text = `Fulfilling Buy request`
      if (depositStatus?.createdTxnHash) {
        status = 'loading'
      }

      if (depositStatus?.executedTxnHash) {
        text = `Buy order executed`
        status = 'success'
        txnHash = depositStatus?.executedTxnHash
      }

      if (depositStatus?.cancelledTxnHash) {
        text = `Buy Order cancelled - ${depositStatus?.reason ? t(`errors:${depositStatus?.reason}`) || depositStatus?.reason : 'Increase slippage tolerance.'}`
        status = 'error'
        txnHash = depositStatus?.cancelledTxnHash
      }
    } else {
      text = `Fulfilling Sell request`

      if (withdrawalStatus?.createdTxnHash) {
        status = 'loading'
      }

      if (withdrawalStatus?.executedTxnHash) {
        text = `Sell order executed`
        status = 'success'
        txnHash = withdrawalStatus?.executedTxnHash
      }
      if (withdrawalStatus?.cancelledTxnHash) {
        text = `Sell Order cancelled - ${withdrawalStatus?.reason ? t(`errors:${withdrawalStatus?.reason}`) || withdrawalStatus?.reason : 'Increase slippage tolerance.'}`
        status = 'error'
        txnHash = withdrawalStatus?.cancelledTxnHash
      }
    }

    return <TransactionStatus status={status} txnHash={txnHash} text={text} />
  }, [depositStatus, isDeposit, withdrawalStatus])

  useEffect(
    function getStatusKey() {
      if (isDeposit) {
        if (depositStatusKey) {
          return
        }

        const matchedStatusKey = Object.values(depositStatuses).find(
          (status) =>
            !status.isViewed &&
            status.data &&
            getPendingDepositKey(status.data) === pendingDepositKey,
        )?.key

        if (matchedStatusKey) {
          setDepositStatusKey(matchedStatusKey)
          setDepositStatusViewed(matchedStatusKey)
        }
      } else {
        if (withdrawalStatusKey) {
          return
        }

        const matchedStatusKey = Object.values(withdrawalStatuses).find(
          (status) =>
            !status.isViewed &&
            status.data &&
            getPendingWithdrawalKey(status.data) === pendingWithdrawalKey,
        )?.key

        if (matchedStatusKey) {
          setWithdrawalStatusKey(matchedStatusKey)
          setWithdrawalStatusViewed(matchedStatusKey)
        }
      }
    },
    [
      depositStatusKey,
      depositStatuses,
      isDeposit,
      pendingDepositKey,
      pendingWithdrawalKey,
      setDepositStatusViewed,
      setWithdrawalStatusViewed,
      toastTimestamp,
      withdrawalStatusKey,
      withdrawalStatuses,
    ],
  )

  useToastAutoClose(isCompleted, toastTimestamp)

  return (
    <StatusNotification title={title} hasError={hasError}>
      <div className="flex flex-col gap-2">
        {creationStatus}
        {executionStatus}
      </div>
    </StatusNotification>
  )
}
